import {
  IonContent,
  IonMenu,
  IonList,
  IonFooter,
  IonToolbar,
  IonListHeader,
  isPlatform,
} from '@ionic/react';
import {
  barChartOutline as barChart,
  bookOutline as book,
  cog,
  homeOutline as home,
  logOutOutline,
  mapOutline,
  peopleOutline as people,
  flagOutline,
  helpCircleOutline,
  shuffleOutline,
} from 'ionicons/icons';
import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../context/UserContext';
import { UserDetail } from '../UserDetail/UserDetail';
import { SidebarItem } from './SidebarItem';
import { useActiveRoute } from '../../hooks/useActiveRoute';

interface SidebarProps {
  onToggle: () => void;
  open: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ onToggle, open }) => {
  const { logout = () => {}, user } = useContext(UserContext);
  const { payment_status, route } = useActiveRoute();
  const { t } = useTranslation();

  const clearSession = useCallback(async () => {
    console.log('Logging out...');
    // Close the sidebar first
    onToggle();
    // Then logout
    await logout();
  }, [logout, onToggle]);

  const sidebarItems = [
    {
      icon: home,
      link: '/dashboard/home',
      name: t('components.Sidebar.Dashboard'),
    },
    {
      icon: barChart,
      link: '/dashboard/progress',
      name: t('components.Sidebar.Your Progress'),
    },
    {
      icon: book,
      link: '/dashboard/passport',
      name: t('components.Sidebar.Passport'),
    },
    {
      icon: mapOutline,
      link: '/dashboard/map',
      name: t('components.Sidebar.Map'),
    },
    {
      icon: flagOutline,
      link: '/dashboard/destinations',
      name: t('components.Sidebar.Destinations'),
    },
    {
      icon: people,
      link: '/dashboard/groups',
      name: t('components.Sidebar.Groups'),
    },
  ];

  const color = isPlatform('desktop') ? '' : '';

  return (
    <IonMenu
      color={color}
      contentId="main"
      disabled={!open}
      side={isPlatform('desktop') ? 'start' : 'end'}
      style={{ width: isPlatform('desktop') ? '240px' : undefined }}
      swipeGesture={false}
      onIonDidClose={onToggle}
      onIonDidOpen={onToggle}
    >
      <IonToolbar
        color="dark"
        style={{
          // @ts-ignore
          '--min-height': isPlatform('desktop') ? '60px' : '0px',
          ...(isPlatform('ios') ? { paddingTop: '30px' } : {}),
        }}
      >
        <UserDetail color={color} size="small" user={user} route={route} />
      </IonToolbar>
      <IonContent color={color}>
        <IonList color={color}>
          <IonListHeader>
            <strong>{route.name}</strong>
          </IonListHeader>
          {sidebarItems.map((item) => (
            <SidebarItem
              key={item.link}
              icon={item.icon}
              link={item.link}
              name={item.name}
            />
          ))}
        </IonList>
      </IonContent>
      <IonFooter style={{ marginBottom: '10px' }}>
        <SidebarItem
          icon={shuffleOutline}
          name={t('components.Sidebar.Routes')}
          link="/dashboard/routes"
        />
        <SidebarItem
          icon={helpCircleOutline}
          name={t('components.Sidebar.Support')}
          href="https://caminoforgood.com/support/"
          target="_blank"
        />
        <SidebarItem
          icon={cog}
          name={t('components.Sidebar.Settings')}
          link="/dashboard/settings"
          badgeValue={payment_status ? undefined : 1}
        />
        <SidebarItem
          icon={logOutOutline}
          name={t('components.Sidebar.Logout')}
          onClick={clearSession}
        />
      </IonFooter>
    </IonMenu>
  );
};
